import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { CopyableSecret } from "../../components/ui/CopyableSecret";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Tabs, type TabDef } from "../../components/ui/Tabs";
import { useNotify } from "../../lib/notify";
import { useTeamMembers, useUpdateTeamMember, useDeleteTeamMember } from "../../features/team/api";
import {
  useActivityPresets,
  useAdminWorkspace,
  useAuditLog,
  useCreateActivityPreset,
  useDeleteActivityPreset,
  useDeleteWorkspace,
  useDeleteWorkspacePage,
  useInviteToWorkspace,
  useLeadForms,
  usePendingInvites,
  useReconcileWorkspace,
  useRevokeInvite,
  useUpdateActivityPreset,
  useUpdatePageForms,
  useUpdateReconciliation,
  useUpdateWorkspaceConfig,
  useUpdateWorkspaceProfile,
  useUpdateWorkspaceToggles,
  type ActivityPreset,
  type AdminWorkspace,
} from "../../features/superadmin/api";

type TabKey =
  | "overview"
  | "members"
  | "reconcile"
  | "pages"
  | "presets"
  | "config"
  | "audit"
  | "danger";

const TABS: TabDef<TabKey>[] = [
  { key: "overview", label: "Overview" },
  { key: "members", label: "Members" },
  { key: "reconcile", label: "Reconcile" },
  { key: "pages", label: "Meta pages" },
  { key: "presets", label: "Activity presets" },
  { key: "config", label: "Config" },
  { key: "audit", label: "Audit log" },
  { key: "danger", label: "Danger zone" },
];

export default function SuperadminWorkspaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabKey | null;
  const [active, setActive] = useState<TabKey>(
    tabParam && TABS.some((t) => t.key === tabParam) ? tabParam : "overview"
  );
  const { data, isLoading, isError, error } = useAdminWorkspace(id);

  const setTab = (key: TabKey) => {
    setActive(key);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  if (isLoading) return <div className="skeleton-rows">Loading…</div>;
  if (isError) return <div className="form-error">{(error as Error).message}</div>;
  if (!data) return null;

  const ws = data.workspace;

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">{ws.name}</h1>
          <p className="page-subtitle">slug: {ws.slug}</p>
        </div>
      </header>

      <Tabs tabs={TABS} active={active} onChange={setTab} />

      <div className="tab-panel">
        {active === "overview" && (
          <>
            <Card title="Workspace">
              <dl className="detail-list">
                <dt>Timezone</dt><dd>{ws.timezone}</dd>
                <dt>Meta App ID</dt><dd>{ws.metaAppId ?? "—"}</dd>
                <dt>Meta App Secret</dt><dd><CopyableSecret value={ws.metaAppSecret} /></dd>
                <dt>Graph API</dt><dd>{ws.metaGraphApiVersion}</dd>
                <dt>Whatrite enabled</dt><dd>{ws.whatriteEnabled ? "yes" : "no"}</dd>
                <dt>Whatmail enabled</dt><dd>{ws.whatmailEnabled ? "yes" : "no"}</dd>
                <dt>Forms API key</dt><dd><CopyableSecret value={ws.formsApiKey} /></dd>
              </dl>
            </Card>
            <Card title="Feature flags" subtitle="Per-workspace toggles that change how the app behaves for non-admin users.">
              <FeatureFlagsEditor workspace={ws} />
            </Card>
            <Card title="Profile" subtitle="Primary contact details. Leave a field empty and save to clear it.">
              <ProfileEditor workspace={ws} />
            </Card>
          </>
        )}

        {active === "members" && (
          <>
            <Card title="Current members">
              <MembersTable workspaceId={ws.id} />
            </Card>
            <Card title="Invite member">
              <InviteForm workspaceId={ws.id} />
            </Card>
            <Card title="Pending invites">
              <PendingInvitesTable workspaceId={ws.id} />
            </Card>
          </>
        )}

        {active === "reconcile" && (
          <>
            <Card title="Reconcile now">
              <ReconcileNowControls workspaceId={ws.id} />
            </Card>
            <Card title="Reconciliation schedule">
              <ReconciliationScheduleEditor
                workspaceId={ws.id}
                initialCron={ws.reconciliationCron}
                initialEnabled={ws.reconciliationEnabled}
              />
            </Card>
          </>
        )}

        {active === "pages" && (
          <Card title="Connected Meta pages">
            <ConnectedPagesTable workspaceId={ws.id} pages={data.pages} />
          </Card>
        )}

        {active === "presets" && (
          <Card title="Activity presets">
            <ActivityPresetsAdmin workspaceId={ws.id} />
          </Card>
        )}

        {active === "config" && (
          <>
            <ConfigEditorCard
              workspaceId={ws.id}
              title="Whatmail config"
              field="whatmailConfig"
              initialValue={ws.whatmailConfig}
            />
            <ConfigEditorCard
              workspaceId={ws.id}
              title="Whatrite config"
              field="whatriteConfig"
              initialValue={ws.whatriteConfig}
            />
          </>
        )}

        {active === "audit" && (
          <Card title="Audit log">
            <AuditLogTable workspaceId={ws.id} />
          </Card>
        )}

        {active === "danger" && (
          <Card title="Danger zone">
            <DeleteWorkspaceControls workspaceId={ws.id} slug={ws.slug} name={ws.name} />
          </Card>
        )}
      </div>
    </>
  );
}

function MembersTable({ workspaceId }: { workspaceId: string }) {
  const notify = useNotify();
  const { data, isLoading, isError, error } = useTeamMembers(workspaceId);
  const update = useUpdateTeamMember(workspaceId);
  const remove = useDeleteTeamMember(workspaceId);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  if (isLoading) return <div className="skeleton-rows">Loading…</div>;
  if (isError) return <div className="form-error">{(error as Error).message}</div>;
  if (!data || data.users.length === 0) {
    return <p className="muted">No members in this workspace yet.</p>;
  }

  return (
    <>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Active</th>
              <th>Last login</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>
                  <select
                    value={u.role}
                    disabled={update.isPending}
                    onChange={(e) => {
                      const role = e.target.value as "sales" | "manager" | "admin";
                      update.mutate(
                        { id: u.id, role },
                        {
                          onSuccess: () => notify.success(`Role updated for ${u.name}`),
                          onError: (err) => notify.error(err),
                        }
                      );
                    }}
                  >
                    <option value="sales">sales</option>
                    <option value="manager">manager</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={u.isActive}
                    disabled={update.isPending}
                    onChange={(e) =>
                      update.mutate(
                        { id: u.id, isActive: e.target.checked },
                        {
                          onSuccess: () =>
                            notify.success(
                              `${u.name} is now ${e.target.checked ? "active" : "inactive"}`
                            ),
                          onError: (err) => notify.error(err),
                        }
                      )
                    }
                  />
                </td>
                <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</td>
                <td>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => setPendingDelete({ id: u.id, name: u.name })}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove this member?"
        body={
          pendingDelete ? (
            <>
              <strong>{pendingDelete.name}</strong> will lose access to this
              workspace immediately. Their lead history is preserved.
            </>
          ) : null
        }
        confirmLabel="Remove member"
        destructive
        busy={remove.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          remove.mutate(target.id, {
            onSuccess: () => notify.success(`Removed ${target.name}`),
            onError: (err) => notify.error(err),
            onSettled: () => setPendingDelete(null),
          });
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}

function ConnectedPagesTable({
  workspaceId,
  pages,
}: {
  workspaceId: string;
  pages: Array<{
    id: string;
    pageId: string;
    pageName: string | null;
    formIds: string[];
    subscribedAt: string | null;
    pageAccessToken: string;
    verifyToken: string;
  }>;
}) {
  const notify = useNotify();
  const remove = useDeleteWorkspacePage(workspaceId);
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    pageId: string;
    pageName: string | null;
  } | null>(null);
  const [expandedPageId, setExpandedPageId] = useState<string | null>(null);

  if (pages.length === 0) {
    return (
      <p className="muted">No pages connected yet — use the Add Workspace wizard.</p>
    );
  }

  return (
    <>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Page</th>
              <th>Page ID</th>
              <th>Forms</th>
              <th>Subscribed</th>
              <th>Access token</th>
              <th>Verify token</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => {
              const isExpanded = expandedPageId === p.pageId;
              return (
                <Fragment key={p.id}>
                  <tr>
                    <td>{p.pageName ?? "—"}</td>
                    <td><code>{p.pageId}</code></td>
                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedPageId(isExpanded ? null : p.pageId)
                        }
                        aria-expanded={isExpanded}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          color: "var(--accent)",
                          cursor: "pointer",
                          font: "inherit",
                        }}
                      >
                        {isExpanded ? "▾" : "▸"} {p.formIds.length}
                      </button>
                    </td>
                    <td>{p.subscribedAt ? new Date(p.subscribedAt).toLocaleString() : "—"}</td>
                    <td><CopyableSecret value={p.pageAccessToken} /></td>
                    <td><CopyableSecret value={p.verifyToken} /></td>
                    <td>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => setPendingDelete(p)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                  {isExpanded ? (
                    <tr>
                      <td colSpan={7}>
                        <PageFormsEditor
                          workspaceId={workspaceId}
                          pageId={p.pageId}
                          pageName={p.pageName}
                          pageAccessToken={p.pageAccessToken}
                          storedFormIds={p.formIds}
                          onClose={() => setExpandedPageId(null)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {remove.isError ? (
        <div className="form-error">{(remove.error as Error).message}</div>
      ) : null}
      <ConfirmDialog
        open={!!pendingDelete}
        title="Soft-delete this page?"
        body={
          pendingDelete ? (
            <>
              <strong>{pendingDelete.pageName ?? pendingDelete.pageId}</strong>{" "}
              will stop receiving webhooks. Reversible by clearing
              <code> deleted_at</code> via SQL.
            </>
          ) : null
        }
        confirmLabel="Remove page"
        destructive
        busy={remove.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          remove.mutate(target.id, {
            onSuccess: () =>
              notify.success(
                `Removed page ${target.pageName ?? target.pageId}`
              ),
            onError: (err) => notify.error(err),
            onSettled: () => setPendingDelete(null),
          });
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}

function PageFormsEditor({
  workspaceId,
  pageId,
  pageName,
  pageAccessToken,
  storedFormIds,
  onClose,
}: {
  workspaceId: string;
  pageId: string;
  pageName: string | null;
  pageAccessToken: string;
  storedFormIds: string[];
  onClose: () => void;
}) {
  const notify = useNotify();
  const { data, isLoading, isError, error } = useLeadForms(pageId, pageAccessToken);
  const update = useUpdatePageForms(workspaceId);
  const [selected, setSelected] = useState<string[]>(storedFormIds);
  const [confirmEmptyOpen, setConfirmEmptyOpen] = useState(false);

  const forms = data?.forms ?? [];
  const dirty = useMemo(() => {
    if (selected.length !== storedFormIds.length) return true;
    const stored = new Set(storedFormIds);
    return selected.some((id) => !stored.has(id));
  }, [selected, storedFormIds]);

  const staleIds = useMemo(() => {
    if (!data) return [] as string[];
    const known = new Set(forms.map((f) => f.id));
    return storedFormIds.filter((id) => !known.has(id));
  }, [data, forms, storedFormIds]);

  const toggle = (id: string, checked: boolean) => {
    setSelected((cur) => {
      const set = new Set(cur);
      if (checked) set.add(id); else set.delete(id);
      return Array.from(set);
    });
  };

  const save = (formIds: string[]) => {
    update.mutate(
      { pageId, formIds },
      {
        onSuccess: () => {
          notify.success(
            `Updated forms for ${pageName ?? pageId} (${formIds.length} selected)`
          );
          setConfirmEmptyOpen(false);
          onClose();
        },
        onError: (err) => notify.error(err),
      }
    );
  };

  const onSave = () => {
    if (selected.length === 0 && storedFormIds.length > 0) {
      setConfirmEmptyOpen(true);
      return;
    }
    save(selected);
  };

  return (
    <div className="form-stack" style={{ padding: "0.75rem 0" }}>
      <p className="muted">
        Forms tracked for <strong>{pageName ?? pageId}</strong>. The reconcile
        cron will poll lead backfill for the selected forms only.
      </p>
      {isLoading ? (
        <div className="skeleton-rows">Loading forms…</div>
      ) : isError ? (
        <div className="form-error">
          Couldn't load forms from Meta: {(error as Error).message}. The page
          access token may have been revoked — re-run the Add Workspace wizard
          to reconnect this page.
        </div>
      ) : forms.length === 0 ? (
        <p className="muted">Meta returned no lead forms for this page.</p>
      ) : (
        <div className="checklist">
          {forms.map((f) => (
            <label key={f.id} className="checklist-item">
              <input
                type="checkbox"
                checked={selected.includes(f.id)}
                onChange={(e) => toggle(f.id, e.target.checked)}
              />
              <div>
                <div className="checklist-title">{f.name}</div>
                <div className="checklist-sub">
                  <code>{f.id}</code>
                  {f.status ? ` · ${f.status}` : ""}
                  {f.leads_count != null ? ` · ${f.leads_count} leads` : ""}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      {staleIds.length > 0 ? (
        <div className="form-error">
          {staleIds.length} stored form ID{staleIds.length === 1 ? "" : "s"} no
          longer returned by Meta (likely deleted/archived):{" "}
          {staleIds.map((id) => (
            <code key={id} style={{ marginRight: "0.5rem" }}>{id}</code>
          ))}
          . Saving will drop them from reconcile.
        </div>
      ) : null}

      {update.isError ? (
        <div className="form-error">{(update.error as Error).message}</div>
      ) : null}

      <div className="dialog-actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={!dirty || update.isPending || isLoading}
        >
          {update.isPending ? "Saving…" : "Save"}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmEmptyOpen}
        title="Save with no forms selected?"
        body={
          <>
            The reconcile cron will stop polling all forms on{" "}
            <strong>{pageName ?? pageId}</strong>. Real-time webhook leads will
            still flow through, but missed leads won't be backfilled.
          </>
        }
        confirmLabel="Save empty selection"
        destructive
        busy={update.isPending}
        onConfirm={() => save([])}
        onCancel={() => setConfirmEmptyOpen(false)}
      />
    </div>
  );
}

function DeleteWorkspaceControls({
  workspaceId,
  slug,
  name,
}: {
  workspaceId: string;
  slug: string;
  name: string;
}) {
  const navigate = useNavigate();
  const notify = useNotify();
  const remove = useDeleteWorkspace();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="form-stack">
      <p className="muted">
        Soft-deletes the workspace. Leads/users/pages stay in the DB but become
        unreachable via the UI. Reversible by clearing <code>deleted_at</code>{" "}
        in psql.
      </p>
      {remove.isError ? (
        <div className="form-error">{(remove.error as Error).message}</div>
      ) : null}
      <div className="dialog-actions">
        <Button
          type="button"
          variant="danger"
          onClick={() => setConfirmOpen(true)}
        >
          Soft-delete workspace
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Soft-delete this workspace?"
        body={
          <>
            <strong>{name}</strong> (<code>{slug}</code>) will disappear from
            listings and its data will become inaccessible via the UI.
            Reversible.
          </>
        }
        confirmLabel="Soft-delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => {
          remove.mutate(workspaceId, {
            onSuccess: () => {
              notify.success(`${name} soft-deleted`);
              setConfirmOpen(false);
              navigate("/superadmin");
            },
            onError: (err) => notify.error(err),
          });
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function AuditLogTable({ workspaceId }: { workspaceId: string }) {
  const { data, isLoading, isError, error } = useAuditLog(workspaceId);
  if (isLoading) return <div className="skeleton-rows">Loading…</div>;
  if (isError) return <div className="form-error">{(error as Error).message}</div>;
  if (!data || data.entries.length === 0) {
    return <p className="muted">No activity recorded yet.</p>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>Target</th>
            <th>Actor</th>
            <th>Metadata</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map((e) => (
            <tr key={e.id}>
              <td>{new Date(e.createdAt).toLocaleString()}</td>
              <td><code>{e.action}</code></td>
              <td>{e.targetType ? `${e.targetType}:${e.targetId ?? "—"}` : "—"}</td>
              <td><code>{e.actorUserId ?? "system"}</code></td>
              <td>
                {e.metadata ? (
                  <code className="muted">{JSON.stringify(e.metadata)}</code>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityPresetsAdmin({ workspaceId }: { workspaceId: string }) {
  const { data, isLoading, isError, error } = useActivityPresets(workspaceId);
  const create = useCreateActivityPreset(workspaceId);
  const update = useUpdateActivityPreset(workspaceId);
  const remove = useDeleteActivityPreset(workspaceId);
  const [newType, setNewType] = useState<ActivityPreset["type"]>("note");
  const [newLabel, setNewLabel] = useState("");

  return (
    <div className="form-stack">
      <p className="muted">
        Workspace-scoped activity presets. Surfaced to users when logging
        activities on a lead.
      </p>

      {isLoading ? (
        <div className="skeleton-rows">Loading…</div>
      ) : isError ? (
        <div className="form-error">{(error as Error).message}</div>
      ) : (data?.presets ?? []).length === 0 ? (
        <p className="muted">No workspace presets yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Label</th>
                <th>Outcome</th>
                <th>Next status</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.presets ?? []).map((p) => (
                <tr key={p.id}>
                  <td><span className="badge">{p.type}</span></td>
                  <td>{p.label}</td>
                  <td>{p.outcome ?? "—"}</td>
                  <td>{p.suggestedNextStatus ?? "—"}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={p.isActive}
                      disabled={update.isPending}
                      onChange={(e) =>
                        update.mutate({ id: p.id, isActive: e.target.checked })
                      }
                    />
                  </td>
                  <td>
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        if (!confirm(`Delete preset "${p.label}"?`)) return;
                        remove.mutate(p.id);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        className="form-stack"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!newLabel.trim()) return;
          await create.mutateAsync({ type: newType, label: newLabel.trim() });
          setNewLabel("");
        }}
      >
        <div className="row-actions">
          <label className="field">
            <span className="field-label">Type</span>
            <select
              className="field-input"
              value={newType}
              onChange={(e) => setNewType(e.target.value as ActivityPreset["type"])}
            >
              <option value="note">note</option>
              <option value="call">call</option>
              <option value="meeting">meeting</option>
              <option value="email">email</option>
              <option value="whatsapp">whatsapp</option>
            </select>
          </label>
          <Input
            label="Label"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Quick follow-up"
          />
          <Button type="submit" disabled={create.isPending || !newLabel.trim()}>
            {create.isPending ? "Adding…" : "Add preset"}
          </Button>
        </div>
        {create.isError ? (
          <div className="form-error">{(create.error as Error).message}</div>
        ) : null}
      </form>
    </div>
  );
}

function PendingInvitesTable({ workspaceId }: { workspaceId: string }) {
  const { data, isLoading, isError, error } = usePendingInvites(workspaceId);
  const revoke = useRevokeInvite(workspaceId);
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (isLoading) return <div className="skeleton-rows">Loading…</div>;
  if (isError) return <div className="form-error">{(error as Error).message}</div>;
  if (!data || data.invites.length === 0) {
    return <p className="muted">No pending invites.</p>;
  }

  return (
    <div className="form-stack">
      {revoke.isError ? (
        <div className="form-error">{(revoke.error as Error).message}</div>
      ) : null}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Invited</th>
              <th>Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.invites.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>{inv.role}</td>
                <td>{new Date(inv.createdAt).toLocaleString()}</td>
                <td>{new Date(inv.expiresAt).toLocaleString()}</td>
                <td>
                  <Button
                    type="button"
                    disabled={revoke.isPending && pendingId === inv.id}
                    onClick={() => {
                      if (!confirm(`Revoke invite to ${inv.email}?`)) return;
                      setPendingId(inv.id);
                      revoke.mutate(inv.id, {
                        onSettled: () => setPendingId(null),
                      });
                    }}
                  >
                    {revoke.isPending && pendingId === inv.id
                      ? "Revoking…"
                      : "Revoke"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeatureFlagsEditor({ workspace }: { workspace: AdminWorkspace }) {
  const notify = useNotify();
  const update = useUpdateWorkspaceToggles(workspace.id);

  const setSalesAutoClaim = (next: boolean) => {
    update.mutate(
      { salesAutoClaim: next },
      {
        onSuccess: () =>
          notify.success(
            next
              ? "Sales can self-claim leads"
              : "Sales can no longer self-claim leads"
          ),
        onError: (err) => notify.error(err),
      }
    );
  };

  return (
    <div className="form-stack">
      <label
        className="field"
        style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
      >
        <input
          type="checkbox"
          checked={workspace.salesAutoClaim}
          disabled={update.isPending}
          onChange={(e) => setSalesAutoClaim(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <div>
          <div>
            <strong>Sales auto-claim</strong>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            When off, sales-role users can't self-assign unassigned leads —
            only managers/admins can transfer leads to them.
          </div>
        </div>
      </label>
      {update.isError ? (
        <div className="form-error">{(update.error as Error).message}</div>
      ) : null}
    </div>
  );
}

function ProfileEditor({ workspace }: { workspace: AdminWorkspace }) {
  const notify = useNotify();
  const update = useUpdateWorkspaceProfile(workspace.id);
  const [primaryEmail, setPrimaryEmail] = useState(workspace.primaryEmail ?? "");
  const [primaryPhone, setPrimaryPhone] = useState(workspace.primaryPhone ?? "");
  const [address, setAddress] = useState(workspace.address ?? "");
  const [logoUrl, setLogoUrl] = useState(workspace.logoUrl ?? "");

  useEffect(() => { setPrimaryEmail(workspace.primaryEmail ?? ""); }, [workspace.primaryEmail]);
  useEffect(() => { setPrimaryPhone(workspace.primaryPhone ?? ""); }, [workspace.primaryPhone]);
  useEffect(() => { setAddress(workspace.address ?? ""); }, [workspace.address]);
  useEffect(() => { setLogoUrl(workspace.logoUrl ?? ""); }, [workspace.logoUrl]);

  const dirty =
    primaryEmail !== (workspace.primaryEmail ?? "") ||
    primaryPhone !== (workspace.primaryPhone ?? "") ||
    address !== (workspace.address ?? "") ||
    logoUrl !== (workspace.logoUrl ?? "");

  return (
    <div className="form-stack">
      <Input
        label="Primary email"
        value={primaryEmail}
        onChange={(e) => setPrimaryEmail(e.target.value)}
        placeholder="ops@example.com"
      />
      <Input
        label="Primary phone"
        value={primaryPhone}
        onChange={(e) => setPrimaryPhone(e.target.value)}
        placeholder="+91 90000 00000"
      />
      <Input
        label="Address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="Street, city, country"
      />
      <Input
        label="Logo URL"
        value={logoUrl}
        onChange={(e) => setLogoUrl(e.target.value)}
        placeholder="https://…"
      />
      {update.isError ? (
        <div className="form-error">{(update.error as Error).message}</div>
      ) : null}
      <div className="dialog-actions">
        <Button
          type="button"
          disabled={!dirty || update.isPending}
          onClick={() => {
            const patch: {
              primaryEmail?: string | null;
              primaryPhone?: string | null;
              address?: string | null;
              logoUrl?: string | null;
            } = {};
            if (primaryEmail !== (workspace.primaryEmail ?? "")) patch.primaryEmail = primaryEmail;
            if (primaryPhone !== (workspace.primaryPhone ?? "")) patch.primaryPhone = primaryPhone;
            if (address !== (workspace.address ?? "")) patch.address = address;
            if (logoUrl !== (workspace.logoUrl ?? "")) patch.logoUrl = logoUrl;
            update.mutate(patch, {
              onSuccess: () => notify.success("Profile updated"),
              onError: (err) => notify.error(err),
            });
          }}
        >
          {update.isPending ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </div>
  );
}

function ReconciliationScheduleEditor({
  workspaceId,
  initialCron,
  initialEnabled,
}: {
  workspaceId: string;
  initialCron: string;
  initialEnabled: boolean;
}) {
  const notify = useNotify();
  const update = useUpdateReconciliation(workspaceId);
  const [cron, setCron] = useState(initialCron);
  const [enabled, setEnabled] = useState(initialEnabled);

  useEffect(() => {
    setCron(initialCron);
  }, [initialCron]);
  useEffect(() => {
    setEnabled(initialEnabled);
  }, [initialEnabled]);

  const dirty = cron.trim() !== initialCron || enabled !== initialEnabled;

  return (
    <div className="form-stack">
      <p className="muted">
        Per-workspace BullMQ scheduler. UTC cron expression (5 fields). The
        scheduler reconciles its repeatable-job set every ~60s; this PATCH
        triggers an immediate refresh too.
      </p>
      <Input
        label="Cron"
        value={cron}
        onChange={(e) => setCron(e.target.value)}
        placeholder="0 * * * *"
        style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
      />
      <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Enabled</span>
      </label>
      {update.isError ? (
        <div className="form-error">{(update.error as Error).message}</div>
      ) : null}
      <div className="dialog-actions">
        <Button
          type="button"
          disabled={!dirty || update.isPending}
          onClick={() => {
            const patch: { reconciliationCron?: string; reconciliationEnabled?: boolean } = {};
            if (cron.trim() !== initialCron) patch.reconciliationCron = cron.trim();
            if (enabled !== initialEnabled) patch.reconciliationEnabled = enabled;
            update.mutate(patch, {
              onSuccess: () => notify.success("Schedule updated"),
              onError: (err) => notify.error(err),
            });
          }}
        >
          {update.isPending ? "Saving…" : "Save schedule"}
        </Button>
      </div>
    </div>
  );
}

function ReconcileNowControls({ workspaceId }: { workspaceId: string }) {
  const notify = useNotify();
  const reconcile = useReconcileWorkspace(workspaceId);

  return (
    <div className="form-stack">
      <p className="muted">
        Queues an immediate BullMQ job for this workspace. Progress shows up
        on <a href="/admin/jobs" target="_blank" rel="noreferrer">/admin/jobs</a>{" "}
        and in worker logs.
      </p>
      {reconcile.isError ? (
        <div className="form-error">{(reconcile.error as Error).message}</div>
      ) : null}
      {reconcile.isSuccess && reconcile.data ? (
        <div className="form-info">
          ✅ Enqueued. Job id: <code>{reconcile.data.jobId ?? "unknown"}</code>
        </div>
      ) : null}
      <div className="dialog-actions">
        <Button
          type="button"
          disabled={reconcile.isPending}
          onClick={() =>
            reconcile.mutate(undefined, {
              onSuccess: (data) =>
                notify.success(
                  `Reconcile queued (job ${data?.jobId ?? "—"})`
                ),
              onError: (err) => notify.error(err),
            })
          }
        >
          {reconcile.isPending ? "Enqueuing…" : "Reconcile now"}
        </Button>
      </div>
    </div>
  );
}

function InviteForm({ workspaceId }: { workspaceId: string }) {
  const invite = useInviteToWorkspace(workspaceId);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"sales" | "manager" | "admin">("sales");

  return (
    <form
      className="form-stack"
      onSubmit={async (e) => {
        e.preventDefault();
        await invite.mutateAsync({ email, role });
        setEmail("");
      }}
    >
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <label className="field">
        <span className="field-label">Role</span>
        <select
          className="field-input"
          value={role}
          onChange={(e) => setRole(e.target.value as "sales" | "manager" | "admin")}
        >
          <option value="sales">sales</option>
          <option value="manager">manager</option>
          <option value="admin">admin</option>
        </select>
      </label>
      {invite.isError ? (
        <div className="form-error">{(invite.error as Error).message}</div>
      ) : null}
      {invite.isSuccess && invite.data ? (
        <div className="form-info">
          ✅ Invite sent. Accept URL:
          <pre className="codeblock">{invite.data.acceptUrl}</pre>
        </div>
      ) : null}
      <div className="dialog-actions">
        <Button type="submit" disabled={invite.isPending}>
          {invite.isPending ? "Sending…" : "Send invite"}
        </Button>
      </div>
    </form>
  );
}

interface ConfigEditorProps {
  workspaceId: string;
  title: string;
  field: "whatmailConfig" | "whatriteConfig";
  initialValue: unknown;
}

function ConfigEditorCard({ workspaceId, title, field, initialValue }: ConfigEditorProps) {
  const update = useUpdateWorkspaceConfig(workspaceId);
  const [draft, setDraft] = useState(() =>
    initialValue == null ? "" : JSON.stringify(initialValue, null, 2)
  );
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initialValue == null ? "" : JSON.stringify(initialValue, null, 2));
  }, [initialValue]);

  return (
    <Card title={title} subtitle="JSON — saved as-is to workspace.{field}">
      <div className="form-stack">
        <textarea
          className="field-input"
          rows={12}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setParseError(null);
          }}
          style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
          spellCheck={false}
        />
        {parseError ? <div className="form-error">{parseError}</div> : null}
        {update.isError ? (
          <div className="form-error">{(update.error as Error).message}</div>
        ) : null}
        {update.isSuccess ? (
          <div className="form-info">✅ Saved.</div>
        ) : null}
        <div className="dialog-actions">
          <Button
            type="button"
            disabled={update.isPending}
            onClick={async () => {
              let parsed: unknown = null;
              if (draft.trim() !== "") {
                try {
                  parsed = JSON.parse(draft);
                } catch (err) {
                  setParseError((err as Error).message);
                  return;
                }
              }
              await update.mutateAsync({ [field]: parsed });
            }}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
